import React, { createContext, useState, useContext, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
const API = `${BACKEND_URL}/api`;

// Shared axios instance with credentials (sends httpOnly cookies automatically)
const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

// Refresh interceptor: on 401, try to refresh the token once before failing
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) reject(error);
    else resolve();
  });
  failedQueue = [];
};

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      // Don't retry refresh or signout requests
      if (originalRequest.url?.includes('/auth/refresh') || originalRequest.url?.includes('/auth/signout')) {
        return Promise.reject(error);
      }

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(() => api(originalRequest));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        await api.post('/auth/refresh');
        processQueue(null);
        return api(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError);
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

export { api };

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const response = await api.get('/auth/profile');
      setUser(response.data);
    } catch (error) {
      setUser(null);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const signup = useCallback(async (fullName, email, password) => {
    try {
      const response = await api.post('/auth/signup', {
        full_name: fullName,
        email,
        password
      });
      setUser(response.data.user);
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Signup failed'
      };
    }
  }, []);

  const signin = useCallback(async (email, password) => {
    try {
      const response = await api.post('/auth/signin', { email, password });
      setUser(response.data.user);
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Sign in failed'
      };
    }
  }, []);

  const googleSignIn = useCallback(async (credential) => {
    try {
      const response = await api.post('/auth/google', { credential });
      setUser(response.data.user);
      return { success: true, isNewUser: response.data.is_new_user };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Google sign in failed'
      };
    }
  }, []);

  const signout = useCallback(async () => {
    try {
      await api.post('/auth/signout');
    } catch (error) {
      // Clear state even if server call fails
    }
    setUser(null);
  }, []);

  const deleteAccount = useCallback(async () => {
    try {
      await api.delete('/auth/account');
      setUser(null);
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Failed to delete account'
      };
    }
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      const response = await api.get('/auth/profile');
      setUser(response.data);
      return { success: true, user: response.data };
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') console.error('Failed to refresh user:', error);
      return { success: false };
    }
  }, []);

  const value = useMemo(() => ({
    user,
    loading,
    signup,
    signin,
    googleSignIn,
    signout,
    deleteAccount,
    refreshUser,
    isAuthenticated: !!user
  }), [user, loading, signup, signin, googleSignIn, signout, deleteAccount, refreshUser]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
